import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { _ } from "@sablier/v2-mixins";
import type { Modal, ModalAction, ModalReducerAction } from "@sablier/v2-types";
import type { ReactNode } from "react";

export const ModalContext = createContext<{
  list: Modal[];
  send: ModalAction;
}>({
  list: [],
  send: () => {},
});

export default function Provider({ children }: { children: ReactNode }) {
  const value = useModals();

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

function reducer(state: Modal[], action: ModalReducerAction) {
  const { payload, type } = action;
  switch (type) {
    case "open": {
      if (_.isNil(state.find((item) => item.id === payload.id))) {
        return [
          ...state,
          {
            id: payload.id,
            isOpen: true,
            data: payload.data || {},
          },
        ];
      }
      return state.map((item) =>
        item.id === payload.id
          ? {
              ...item,
              isOpen: true,
              data: payload.data || item.data,
            }
          : item,
      );
    }
    case "close":
      return state.map((item) =>
        item.id === payload.id ? { ...item, isOpen: false } : item,
      );
    case "set":
      return state.map((item) => (item.id === payload.id ? payload : item));
    case "update":
      return state.map((item) =>
        item.id === payload.id ? _.mergeWith(item, payload) : item,
      );
    case "reset":
      return [];
    default:
      return state;
  }
}

function useModals() {
  const [list, dispatch] = useReducer(reducer, []);

  const send: ModalAction = useCallback(
    (type, payload) => {
      const params = { type, payload } as ModalReducerAction;
      dispatch(params);
    },
    [dispatch],
  );

  return {
    list,
    send,
  };
}

export function useModalContext() {
  return useContext(ModalContext);
}

export function useModal<T extends object>(id: string) {
  const { list, send } = useModalContext();

  const modal = useMemo(
    () => list.find((item) => item.id === id),
    [list, id],
  ) as Modal<T>;

  const { isOpen, data } = useMemo(
    () => modal || { isOpen: false, data: {} },
    [modal],
  ) as { isOpen: boolean; data: T };

  const setOpen = useCallback(
    (state = true, data?: T) => {
      if (state) {
        send("open", { id, data });
      } else {
        send("close", { id, data });
      }
    },
    [send, id],
  );

  const setData = useCallback(
    (data: T) => {
      send("set", { id, data });
    },
    [send, id],
  );

  const updateData = useCallback(
    (data: Partial<T>) => {
      send("update", { id, data });
    },
    [send, id],
  );

  const doClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const doOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const doReset = useCallback(() => {
    send("reset", {});
  }, [send]);

  return {
    doClose,
    doOpen,
    doReset,
    id,
    isOpen,
    setOpen,
    setData,
    updateData,
    data,
  };
}
